/**
 * Agent Ticker component.
 *
 * Renders a compact, hierarchy-aware row of top-level agent pills.
 * Nested child agents stay hidden from the main bar and are instead
 * discoverable from the peek drawer's expandable tree view.
 *
 * Features:
 * - Top-level/root pills only in the main bar
 * - Passive child indicators on root pills
 * - Tap to peek at recent messages (existing behavior preserved)
 * - Expandable hierarchy tree inside the peek drawer
 * - Context menu: long press (touch) or hover three-dot button (mouse)
 * - Live streaming peek overlay still supported
 * - Multi-gateway aware
 */

import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import type { TickerItem, PeekData } from '../hooks/useAgentTicker';
import { normalizeContent } from '../utils/content';
import styles from './AgentTicker.module.css';

// ── Pointer detection ──

/**
 * Detect whether the device has a fine pointer (mouse) vs coarse (touch).
 * Falls back to touch if matchMedia is unavailable.
 */
function useHasFinePointer(): boolean {
  const [hasFine, setHasFine] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia?.('(pointer: fine)')?.matches ?? false;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia?.('(pointer: fine)');
    if (!mql) return;

    const handler = (e: MediaQueryListEvent) => setHasFine(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  return hasFine;
}

// ── Hierarchy helpers ──

type DisplayStatus = 'active' | 'idle' | 'attention';

interface HierarchyNode {
  item: TickerItem;
  parent: HierarchyNode | null;
  children: HierarchyNode[];
  depth: number;
  directChildCount: number;
  totalDescendantCount: number;
  aggregateStatus: DisplayStatus;
  aggregateHeatLevel: number;
  latestActivityAt: number;
  displayLabel: string;
}

interface HierarchyModel {
  roots: HierarchyNode[];
  nodeById: Map<string, HierarchyNode>;
}

function displayStatusForItem(item: TickerItem): DisplayStatus {
  if (item.status === 'error') return 'attention';
  if (!item.sessionGone && (item.status === 'running' || item.heatLevel > 0)) {
    return 'active';
  }
  return 'idle';
}

function statusPriority(status: DisplayStatus): number {
  switch (status) {
    case 'attention':
      return 0;
    case 'active':
      return 1;
    case 'idle':
      return 2;
  }
}

function compareNodes(a: HierarchyNode, b: HierarchyNode): number {
  const statusDelta = statusPriority(a.aggregateStatus) - statusPriority(b.aggregateStatus);
  if (statusDelta !== 0) return statusDelta;
  return b.latestActivityAt - a.latestActivityAt;
}

function buildHierarchy(items: TickerItem[]): HierarchyModel {
  const nodeById = new Map<string, HierarchyNode>();
  const nodeByCompositeSession = new Map<string, HierarchyNode>();

  for (const item of items) {
    const node: HierarchyNode = {
      item,
      parent: null,
      children: [],
      depth: 0,
      directChildCount: 0,
      totalDescendantCount: 0,
      aggregateStatus: displayStatusForItem(item),
      aggregateHeatLevel: item.heatLevel,
      latestActivityAt: item.updatedAt ?? item.firstSeenAt,
      displayLabel: item.label,
    };

    nodeById.set(item.id, node);
    nodeByCompositeSession.set(`${item.gatewayId}::${item.sessionKey}`, node);
  }

  const roots: HierarchyNode[] = [];

  for (const node of nodeById.values()) {
    const parent = node.item.parentSessionKey
      ? nodeByCompositeSession.get(`${node.item.gatewayId}::${node.item.parentSessionKey}`) ?? null
      : null;

    if (parent && parent !== node) {
      node.parent = parent;
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  }

  const decorate = (node: HierarchyNode, depth: number): void => {
    node.depth = depth;

    for (const child of node.children) {
      decorate(child, depth + 1);
    }

    node.children.sort(compareNodes);

    node.directChildCount = node.children.length;
    node.totalDescendantCount = node.children.reduce(
      (sum, child) => sum + 1 + child.totalDescendantCount,
      0,
    );

    node.aggregateHeatLevel = node.children.reduce(
      (maxHeat, child) => Math.max(maxHeat, child.aggregateHeatLevel),
      node.item.heatLevel,
    );

    node.latestActivityAt = node.children.reduce(
      (latest, child) => Math.max(latest, child.latestActivityAt),
      node.item.updatedAt ?? node.item.firstSeenAt,
    );

    if (node.children.some((child) => child.aggregateStatus === 'attention')) {
      node.aggregateStatus = 'attention';
    } else if (
      displayStatusForItem(node.item) === 'active'
      || node.children.some((child) => child.aggregateStatus === 'active')
    ) {
      node.aggregateStatus = 'active';
    } else {
      node.aggregateStatus = 'idle';
    }
  };

  for (const root of roots) {
    decorate(root, 0);
  }

  roots.sort(compareNodes);

  return { roots, nodeById };
}

function statusLabel(status: DisplayStatus): string {
  switch (status) {
    case 'attention':
      return 'attention';
    case 'active':
      return 'active';
    case 'idle':
      return 'idle';
  }
}

function statusBadgeClass(status: DisplayStatus): string {
  switch (status) {
    case 'attention':
      return styles.statusBadgeAttention;
    case 'active':
      return styles.statusBadgeActive;
    case 'idle':
      return styles.statusBadgeIdle;
  }
}

function statusMarkerClass(status: DisplayStatus): string {
  switch (status) {
    case 'attention':
      return styles.statusMarkerAttention;
    case 'active':
      return styles.statusMarkerActive;
    case 'idle':
      return styles.statusMarkerIdle;
  }
}

function childCountLabel(count: number): string {
  return count === 1 ? '1 child agent' : `${count} child agents`;
}

// ── Context Menu ──

interface ContextMenuState {
  /** The ticker item this menu is for */
  item: TickerItem;
  /** Position to render the menu */
  x: number;
  y: number;
}

interface ContextMenuProps {
  state: ContextMenuState;
  isSubscribed: boolean;
  onFocus: (item: TickerItem) => void;
  onSubscribe: (sessionKey: string) => void;
  onUnsubscribe: (sessionKey: string) => void;
  onDismiss: (id: string) => void;
  onClose: () => void;
}

function TickerContextMenu({ state, isSubscribed, onFocus, onSubscribe, onUnsubscribe, onDismiss, onClose }: ContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    const handler = (e: MouseEvent | TouchEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    // Use a slight delay to prevent the opening event from immediately closing the menu
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handler);
      document.addEventListener('touchstart', handler);
    }, 50);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // Adjust position to stay within viewport
  const style = useMemo(() => {
    const menuWidth = 180;
    const menuHeight = 180;
    let left = state.x;
    let top = state.y;

    if (typeof window !== 'undefined') {
      if (left + menuWidth > window.innerWidth - 8) {
        left = window.innerWidth - menuWidth - 8;
      }
      if (left < 8) left = 8;

      // If menu would go below viewport, show above the trigger
      if (top + menuHeight > window.innerHeight - 8) {
        top = state.y - menuHeight - 8;
      }
      if (top < 8) top = 8;
    }

    return { left, top };
  }, [state.x, state.y]);

  return (
    <div
      ref={menuRef}
      className={styles.contextMenu}
      style={{ left: style.left, top: style.top }}
      role="menu"
    >
      <button
        className={styles.contextMenuItem}
        role="menuitem"
        onClick={() => {
          onFocus(state.item);
          onClose();
        }}
      >
        🔍 Focus
      </button>
      {isSubscribed ? (
        <button
          className={styles.contextMenuItem}
          role="menuitem"
          onClick={() => {
            onUnsubscribe(state.item.sessionKey);
            onClose();
          }}
        >
          🙈 Hide from main chat
        </button>
      ) : (
        <button
          className={styles.contextMenuItem}
          role="menuitem"
          onClick={() => {
            onSubscribe(state.item.sessionKey);
            onClose();
          }}
        >
          👁 Show in main chat
        </button>
      )}
      <button
        className={styles.contextMenuItem}
        role="menuitem"
        onClick={() => {
          onDismiss(state.item.id);
          onClose();
        }}
      >
        ✕ Dismiss
      </button>
      <button
        className={`${styles.contextMenuItem} ${styles.contextMenuItemDisabled}`}
        role="menuitem"
        disabled
        title="Coming soon"
      >
        🔮 Pin as orb
      </button>
    </div>
  );
}

// ── Sub-components ──

interface TickerPillProps {
  node: HierarchyNode;
  isSubscribed: boolean;
  hasFinePointer: boolean;
  onTap: (item: TickerItem) => void;
  onContextMenu: (item: TickerItem, x: number, y: number) => void;
}

function TickerPill({ node, isSubscribed, hasFinePointer, onTap, onContextMenu }: TickerPillProps) {
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressFiredRef = useRef(false);
  const touchStartPosRef = useRef<{ x: number; y: number } | null>(null);

  const { item } = node;
  const displayStatus = node.aggregateStatus;
  const hiddenChildCount = node.totalDescendantCount;
  const visibleLabel = node.displayLabel;

  const statusClass = `
    ${displayStatus === 'attention'
      ? styles.tickerPillAttention
      : displayStatus === 'active'
        ? styles.tickerPillActive
        : styles.tickerPillIdle
    }${isSubscribed ? ` ${styles.tickerPillSubscribed}` : ''}`;

  const heatClass = styles[`heat${node.aggregateHeatLevel}`] ?? '';
  const visibleDots = Math.min(hiddenChildCount, 4);
  const overflowDots = Math.max(hiddenChildCount - visibleDots, 0);

  // ── Touch: long press to open context menu ──
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      longPressFiredRef.current = false;
      const touch = e.touches[0];
      touchStartPosRef.current = { x: touch.clientX, y: touch.clientY };

      longPressTimerRef.current = setTimeout(() => {
        longPressFiredRef.current = true;
        // Vibrate if available for haptic feedback
        if (navigator.vibrate) navigator.vibrate(30);
        const pos = touchStartPosRef.current;
        if (pos) {
          onContextMenu(item, pos.x, pos.y - 50); // above finger
        }
      }, 500);
    },
    [item, onContextMenu],
  );

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    // Cancel long press if finger moves too far
    if (!touchStartPosRef.current) return;
    const touch = e.touches[0];
    const dx = touch.clientX - touchStartPosRef.current.x;
    const dy = touch.clientY - touchStartPosRef.current.y;
    if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    touchStartPosRef.current = null;
  }, []);

  const handleClick = useCallback(() => {
    // Don't open peek if long press just fired
    if (longPressFiredRef.current) {
      longPressFiredRef.current = false;
      return;
    }
    onTap(item);
  }, [item, onTap]);

  // ── Desktop: three-dot button for context menu ──
  const handleDotsClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      onContextMenu(item, rect.left, rect.bottom + 4);
    },
    [item, onContextMenu],
  );

  // Prevent native context menu on the pill
  const handleNativeContextMenu = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      onContextMenu(item, e.clientX, e.clientY);
    },
    [item, onContextMenu],
  );

  return (
    <button
      className={`${styles.tickerPill} ${statusClass} ${heatClass}`.trim()}
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onContextMenu={handleNativeContextMenu}
      aria-label={`${visibleLabel} — ${statusLabel(displayStatus)}${hiddenChildCount > 0 ? `, ${childCountLabel(hiddenChildCount)}` : ''}`}
      title={`${visibleLabel} (${item.gatewayName}) — ${statusLabel(displayStatus)}${hiddenChildCount > 0 ? ` · ${childCountLabel(hiddenChildCount)}` : ''}`}
    >
      <span className={`${styles.statusMarker} ${statusMarkerClass(displayStatus)}`} aria-hidden="true" />

      <span className={styles.pillBody}>
        <span className={styles.tickerLabel}>{visibleLabel}</span>
        <span className={styles.pillMetaRow}>
          <span className={`${styles.statusBadge} ${statusBadgeClass(displayStatus)}`}>
            {statusLabel(displayStatus)}
          </span>

          {hiddenChildCount > 0 && (
            <span className={styles.childIndicators} aria-hidden="true">
              {Array.from({ length: visibleDots }).map((_, idx) => (
                <span key={idx} className={styles.childDot} />
              ))}
              {overflowDots > 0 && (
                <span className={styles.childOverflow}>+{overflowDots}</span>
              )}
            </span>
          )}

          {isSubscribed && <span className={styles.subscribedBadge}>👁</span>}
        </span>
      </span>

      {hasFinePointer && (
        <span
          className={styles.tickerDotsBtn}
          onClick={handleDotsClick}
          role="button"
          aria-label={`Options for ${visibleLabel}`}
          tabIndex={-1}
        >
          ⋮
        </span>
      )}
    </button>
  );
}

interface HierarchyTreeNodeProps {
  node: HierarchyNode;
  expanded: Set<string>;
  onToggle: (id: string) => void;
  onNavigate: (item: TickerItem) => void;
}

function HierarchyTreeNode({ node, expanded, onToggle, onNavigate }: HierarchyTreeNodeProps) {
  const hasChildren = node.children.length > 0;
  const isExpanded = expanded.has(node.item.id);

  return (
    <div className={styles.treeBranch}>
      <div className={styles.treeRow}>
        {hasChildren ? (
          <button
            type="button"
            className={styles.treeToggleBtn}
            onClick={() => onToggle(node.item.id)}
            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${node.displayLabel}`}
            aria-expanded={isExpanded}
          >
            {isExpanded ? '▾' : '▸'}
          </button>
        ) : (
          <span className={styles.treeToggleSpacer} aria-hidden="true" />
        )}

        <button
          type="button"
          className={styles.treeNodeButton}
          onClick={() => onNavigate(node.item)}
        >
          <span className={styles.treeNodeText}>
            <span className={styles.treeNodeLabel}>{node.displayLabel}</span>
            <span className={styles.treeNodeMeta}>
              {hasChildren ? childCountLabel(node.children.length) : 'Leaf agent'}
            </span>
          </span>

          <span className={`${styles.statusBadge} ${styles.treeStatusBadge} ${statusBadgeClass(node.aggregateStatus)}`}>
            {statusLabel(node.aggregateStatus)}
          </span>
        </button>
      </div>

      {hasChildren && isExpanded && (
        <div className={styles.treeChildren}>
          {node.children.map((child) => (
            <HierarchyTreeNode
              key={child.item.id}
              node={child}
              expanded={expanded}
              onToggle={onToggle}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Peek Overlay ──

interface PeekOverlayProps {
  peek: PeekData;
  hierarchy: HierarchyModel;
  onNavigate: (item: TickerItem) => void;
  onClose: () => void;
}

function PeekOverlay({ peek, hierarchy, onNavigate, onClose }: PeekOverlayProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const touchStartRef = useRef<{ x: number; time: number } | null>(null);
  const userScrolledUpRef = useRef(false);
  const userInteractingRef = useRef(false);
  const interactionTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const [treeOpen, setTreeOpen] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  const currentNode = hierarchy.nodeById.get(peek.item.id) ?? null;
  const parentNode = currentNode?.parent ?? null;
  const currentStatus = currentNode?.aggregateStatus ?? displayStatusForItem(peek.item);
  const totalChildren = currentNode?.totalDescendantCount ?? 0;
  const isStreaming = peek.streamingContent !== null;

  useEffect(() => {
    setTreeOpen(false);
    setExpandedNodes(new Set());
  }, [peek.item.id]);

  // Track user-initiated scroll interactions on the peek body
  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return;

    const markInteracting = () => {
      userInteractingRef.current = true;
      if (interactionTimeoutRef.current) clearTimeout(interactionTimeoutRef.current);
      interactionTimeoutRef.current = setTimeout(() => {
        userInteractingRef.current = false;
      }, 150);
    };

    body.addEventListener('wheel', markInteracting, { passive: true });
    body.addEventListener('touchstart', markInteracting, { passive: true });
    body.addEventListener('touchmove', markInteracting, { passive: true });

    return () => {
      body.removeEventListener('wheel', markInteracting);
      body.removeEventListener('touchstart', markInteracting);
      body.removeEventListener('touchmove', markInteracting);
      if (interactionTimeoutRef.current) clearTimeout(interactionTimeoutRef.current);
    };
  }, []);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Auto-scroll to bottom when new content arrives
  useEffect(() => {
    const body = bodyRef.current;
    if (!body || userScrolledUpRef.current) return;

    body.scrollTop = body.scrollHeight;
  }, [peek.messages, peek.streamingContent]);

  // Detect when user manually scrolls up (to pause auto-scroll)
  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = body;
      const atBottom = scrollHeight - scrollTop - clientHeight < 40;

      if (atBottom) {
        userScrolledUpRef.current = false;
      } else if (userInteractingRef.current) {
        userScrolledUpRef.current = true;
      }
    };

    body.addEventListener('scroll', handleScroll, { passive: true });
    return () => body.removeEventListener('scroll', handleScroll);
  }, []);

  // Swipe right to dismiss (horizontal — no conflict with vertical scrolling)
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartRef.current = {
      x: e.touches[0].clientX,
      time: Date.now(),
    };
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (!touchStartRef.current) return;
      const deltaX = e.changedTouches[0].clientX - touchStartRef.current.x;
      const deltaTime = Date.now() - touchStartRef.current.time;

      if (deltaX > 60 && deltaTime < 500) {
        onClose();
      }
      touchStartRef.current = null;
    },
    [onClose],
  );

  const toggleTreeNode = useCallback((id: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  return (
    <>
      {/* Backdrop — tap to close */}
      <div className={styles.peekBackdrop} onClick={onClose} />

      {/* Panel */}
      <div
        ref={panelRef}
        className={styles.peekPanel}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div className={styles.peekHeader}>
          <div className={styles.peekHeaderMain}>
            <div className={styles.peekTitleBlock}>
              <span className={styles.peekTitle}>🔧 {currentNode?.displayLabel ?? peek.item.label}</span>
              <div className={styles.peekMetaRow}>
                <span className={`${styles.statusBadge} ${statusBadgeClass(currentStatus)}`}>
                  {statusLabel(currentStatus)}
                </span>
                <span className={styles.peekGateway}>{peek.item.gatewayName}</span>
                {totalChildren > 0 && (
                  <span className={styles.peekMetaText}>{childCountLabel(totalChildren)}</span>
                )}
              </div>
            </div>
          </div>

          <button className={styles.peekCloseBtn} onClick={onClose}>
            ✕
          </button>
        </div>

        <div ref={bodyRef} className={styles.peekBody}>
          {parentNode && (
            <button
              type="button"
              className={styles.parentLink}
              onClick={() => onNavigate(parentNode.item)}
            >
              ↑ Spawned by {parentNode.displayLabel}
            </button>
          )}

          <div className={styles.peekSection}>
            <div className={styles.peekSectionTitle}>Recent messages</div>

            {peek.loading && peek.messages.length === 0 ? (
              <div className={styles.peekLoading}>Loading messages…</div>
            ) : peek.error && peek.messages.length === 0 ? (
              <div className={styles.peekError}>{peek.error}</div>
            ) : peek.messages.length === 0 && !isStreaming ? (
              <div className={styles.peekEmpty}>No messages yet</div>
            ) : (
              <>
                {peek.messages.map((msg, idx) => (
                  <div key={idx} className={styles.peekMessage}>
                    <div
                      className={`${styles.peekMessageRole} ${
                        msg.role === 'assistant' ? styles.peekMessageRoleAssistant : ''
                      }`}
                    >
                      {msg.role}
                    </div>
                    <div className={styles.peekMessageContent}>
                      {truncateContent(normalizeContent(msg.content))}
                    </div>
                  </div>
                ))}

                {isStreaming && (
                  <div className={`${styles.peekMessage} ${styles.peekMessageStreaming}`}>
                    <div
                      className={`${styles.peekMessageRole} ${styles.peekMessageRoleAssistant}`}
                    >
                      assistant
                    </div>
                    <div className={styles.peekMessageContent}>
                      {truncateContent(peek.streamingContent!)}
                      <span className={styles.streamingCursor} />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <div className={styles.peekSection}>
            <button
              type="button"
              className={styles.treeAccordion}
              onClick={() => setTreeOpen((prev) => !prev)}
              aria-expanded={treeOpen}
            >
              <span className={styles.treeAccordionLabel}>Child agents</span>
              <span className={styles.treeAccordionCount}>{totalChildren}</span>
              <span className={styles.treeAccordionIcon}>{treeOpen ? '▾' : '▸'}</span>
            </button>

            {treeOpen && (
              <div className={styles.treeBody}>
                {currentNode && currentNode.children.length > 0 ? (
                  currentNode.children.map((child) => (
                    <HierarchyTreeNode
                      key={child.item.id}
                      node={child}
                      expanded={expandedNodes}
                      onToggle={toggleTreeNode}
                      onNavigate={onNavigate}
                    />
                  ))
                ) : (
                  <div className={styles.treeEmpty}>No child agents yet</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

/** Truncate message content for peek display */
function truncateContent(content: string): string {
  const maxLen = 500;
  if (content.length <= maxLen) return content;
  return content.slice(0, maxLen) + '…';
}

// ── Main Component ──

export interface AgentTickerProps {
  items: TickerItem[];
  peek: PeekData | null;
  hasCompleted?: boolean;
  subscribedSessions: Set<string>;
  onItemTap: (item: TickerItem) => void;
  onItemDismiss: (id: string) => void;
  onDismissAllCompleted?: () => void;
  onPeekClose: () => void;
  onFocus: (item: TickerItem) => void;
  onSubscribe: (sessionKey: string) => void;
  onUnsubscribe: (sessionKey: string) => void;
}

/**
 * Renders the agent ticker — a compact horizontal row of root-level agents.
 * Child agents stay off the main bar and are discoverable from the peek tree.
 */
export function AgentTicker({
  items,
  peek,
  hasCompleted,
  subscribedSessions,
  onItemTap,
  onItemDismiss,
  onDismissAllCompleted,
  onPeekClose,
  onFocus,
  onSubscribe,
  onUnsubscribe,
}: AgentTickerProps) {
  const hasFinePointer = useHasFinePointer();
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const hierarchy = useMemo(() => buildHierarchy(items), [items]);

  const openContextMenu = useCallback((item: TickerItem, x: number, y: number) => {
    setContextMenu({ item, x, y });
  }, []);

  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  if (hierarchy.roots.length === 0) return null;

  return (
    <>
      <div className={styles.tickerContainer}>
        {hierarchy.roots.map((node) => (
          <TickerPill
            key={node.item.id}
            node={node}
            isSubscribed={subscribedSessions.has(node.item.sessionKey)}
            hasFinePointer={hasFinePointer}
            onTap={onItemTap}
            onContextMenu={openContextMenu}
          />
        ))}

        {hasCompleted && onDismissAllCompleted && (
          <button
            className={styles.dismissAllBtn}
            onClick={onDismissAllCompleted}
            aria-label="Dismiss all completed"
            title="Dismiss all completed"
          >
            ✕
          </button>
        )}
      </div>

      {contextMenu && (
        <TickerContextMenu
          state={contextMenu}
          isSubscribed={subscribedSessions.has(contextMenu.item.sessionKey)}
          onFocus={onFocus}
          onSubscribe={onSubscribe}
          onUnsubscribe={onUnsubscribe}
          onDismiss={onItemDismiss}
          onClose={closeContextMenu}
        />
      )}

      {peek && (
        <PeekOverlay
          peek={peek}
          hierarchy={hierarchy}
          onNavigate={onItemTap}
          onClose={onPeekClose}
        />
      )}
    </>
  );
}
